// B130 Wave 2C Phase 8 / Phase 9 — pure helpers for the low-value pool
// ledger and FY anchoring. Extracted from `src/components/tax/TaxPage.tsx`
// in Phase 9 so that `src/components/portfolio/PropertyDetailPage.tsx`
// (Mark-As-Sold disposal flow) can read the same SSOT without dragging
// TaxPage into the main bundle (TaxPage is lazy-imported in App.tsx).

import type { Div40Item, LowValuePool } from "../types/portfolio";

/**
 * Australian FY anchor: months Jul (6) – Jun (5) inclusive. April 2026
 * → FY 2025-26 → returns `2025`. UTC-anchored (Wave 2B parity).
 */
export function parseFyFromDate(dateStr: string): number {
  const d = new Date(dateStr + "T00:00:00Z");
  const month = d.getUTCMonth();
  const year = d.getUTCFullYear();
  return month < 6 ? year - 1 : year;
}

/**
 * FY label for an arbitrary ISO date string. Sibling to `parseFyFromDate`
 * — returns the canonical `YYYY-YY` label (e.g. "2025-26" for any
 * date between 1 Jul 2025 and 30 Jun 2026 inclusive). Used by
 * PropertyDetailPage Phase 9 disposal flow to align the pool ledger
 * write-off entry with the disposal FY.
 */
export function fyLabelFromDate(dateStr: string): string {
  const startYear = parseFyFromDate(dateStr);
  return `${startYear}-${String(startYear + 1).slice(2)}`;
}

/**
 * B130 Wave 2C Phase 8 — derive the low-value pool ledger from
 * `Div40Item.poolAllocated` items. Per L48: pool ledger is derived
 * state, not lodged-return ground truth — re-runs on every render
 * (matches Phase 7 decay panel pattern). The existing
 * `depreciationClaimed` ledger remains untouched as the lodged SSOT.
 *
 * Walk-forward per ITAA s40-440 / RPG p45:
 *   firstYearDecline   = additionsThisFY × 0.1875 (18.75%)
 *   subsequentDecline  = openingBalance  × 0.375  (37.5%)
 *   closingBalance(FY) = openingBalance + additionsThisFY − decline
 *   openingBalance(FY+1) = closingBalance(FY)
 *
 * No pro-rata — pool decline applies regardless of mid-year acquisition.
 *
 * Stops at closing < $1 (pool exhausted) OR +5 FYs after the latest
 * addition, whichever comes first.
 */
export function recomputeLowValuePools(items: Div40Item[]): LowValuePool[] {
  const pooledItems = items.filter(
    (i) => i.poolAllocated && i.poolAllocationDate,
  );
  if (pooledItems.length === 0) return [];

  const allocationFys = pooledItems.map((i) =>
    parseFyFromDate(i.poolAllocationDate!),
  );
  const startFyYear = Math.min(...allocationFys);
  const latestFyYear = Math.max(...allocationFys);
  const endFyYear = latestFyYear + 5;

  const pools: LowValuePool[] = [];
  let openingBalance = 0;
  for (let y = startFyYear; y <= endFyYear; y++) {
    const fy = `${y}-${String(y + 1).slice(2)}`;
    const additionsThisFY = pooledItems
      .filter((i) => parseFyFromDate(i.poolAllocationDate!) === y)
      .reduce((sum, i) => sum + i.cost, 0);

    if (openingBalance === 0 && additionsThisFY === 0) continue;

    const firstYearDecline = additionsThisFY * 0.1875;
    const subsequentDecline = openingBalance * 0.375;
    const decline = firstYearDecline + subsequentDecline;
    const closingBalance = Math.max(
      0,
      openingBalance + additionsThisFY - decline,
    );

    pools.push({
      id: `pool-${fy}`,
      fy,
      openingBalance,
      additionsThisFY,
      closingBalance,
    });

    openingBalance = closingBalance;
    if (closingBalance < 1) break;
  }

  return pools;
}

/**
 * B141 — Mandatory low-value pool auto-tick predicate.
 *
 * Per ITAA s40-425 and ATO RPG 2025 p45, once a depreciating asset
 * costing less than $1,000 has been allocated to a property's
 * low-value pool, all later sub-$1,000 assets on that property MUST
 * also be allocated to the pool ("mandatory pooling rule").
 *
 * AssetPicker Step 4 reads this predicate at mount + on cost change
 * to derive the default state of the pool toggle. The user can
 * manually un-tick (override) — that path persists `poolAllocated:
 * false` indistinguishable from a never-pooled item; the audit-risk
 * warning is surfaced inline next to the toggle.
 *
 * Cost band $300–$999 only — sub-$300 items go to immediate
 * deduction (s40-80(2)); $1,000+ items are pool-ineligible.
 *
 * Returns true when the auto-tick rule fires; false otherwise.
 */
export function shouldAutoPoolAllocate(
  cost: number,
  hasExistingPooledItem: boolean,
): boolean {
  return cost >= 300 && cost < 1000 && hasExistingPooledItem;
}
